import React, { useState } from "react";
import { useNotification } from "../../components/framework/NotificationContext";
import ApiClient from "../../services/apiClient";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import { OverlayTrigger, Popover } from "react-bootstrap";

import { useNavigate, Link } from 'react-router-dom';
import SolutionPreparationModal from "./modals/SolutionPreparationModal";
import SolutionPreparationSummary from "./summaries/SolutionPreparationSummary";
import { CHANNEL_LABELS, channelsForNeedle, setupChannels } from "./solutionText";
import CollectorModal from "./modals/CollectorModal";
import CollectorSummary from "./summaries/CollectorSummary";
import NeedleModal from "./modals/NeedleModal";
import NeedleSummary from "./summaries/NeedleSummary";
import FiberMorphologyModal from "./modals/FiberMorphologyModal";
import FiberMorphologySummary from "./summaries/FiberMorphologySummary";
import ProcessInstabilityModal from "./modals/ProcessInstabilityModal";
import PropertyAnalysisModal from "./modals/PropertyAnalysisModal";
import PropertySummary from "./summaries/PropertySummary";
import InstabilitySummary from "./summaries/InstabilitySummary";

import initialRows from "./electrospinningData";
import UploadImageModal from "./modals/UploadImageModal";
import FiberImagesSummary from "./summaries/FiberImageSummary";

// Helper to Create a New Group
const createNewGroup = (id) => {
    const template = initialRows[0];
    return {
        id: id,
        setup: {
            polymerProperty: { ...template.polymerProperty, polymerComponents: [] },
            solventProperty: { ...template.solventProperty, solventComponents: [] },
            solutionProperty: { ...template.solutionProperty },
            collectorProperty: { ...template.collectorProperty },
            needleProperty: { ...template.needleProperty },
            additives: [],
            solutions: null, // set when the spun solution is a mix of separately prepared solutions
        },
        rows: [
            {
                id: 1,
                processParameter: { ...template.processParameter, instability: [] },
                ambientParameter: { ...template.ambientParameter },
                fiberProperty: { ...template.fiberProperty, fiberMorphology: [] },
                mechanicalProperty: { ...template.mechanicalProperty },
                functionalProperty: { ...template.functionalProperty },
                applicationProperty: { ...template.applicationProperty },
                fiberImages: [],
                status: 'PENDING'
            }
        ]
    };
};

// Helper to Create a New Row for a Group
const createNewRow = (id) => {
    const template = initialRows[0];
    return {
        id: id,
        processParameter: { ...template.processParameter, instability: [] },
        ambientParameter: { ...template.ambientParameter },
        fiberProperty: { ...template.fiberProperty, fiberMorphology: [] },
        mechanicalProperty: { ...template.mechanicalProperty },
        functionalProperty: { ...template.functionalProperty },
        applicationProperty: { ...template.applicationProperty },
        fiberImages: [],
        status: 'PENDING'
    };
};

// Run-table columns that hold a typed value, in the order they appear. Pasting a
// block of cells from a spreadsheet fills these left to right. Unit lists match
// the bulk-upload template (first = default).
const RUN_COLUMNS = [
    { parent: 'processParameter', key: 'voltage', label: 'Voltage', unitKey: 'voltageUnit', units: ['kV', 'V'], required: true, minWidth: 130 },
    { parent: 'processParameter', key: 'flowRate', label: 'Flow Rate', unitKey: 'flowRateUnit', units: ['mL/h', 'µL/min'], required: true, minWidth: 140 },
    { parent: 'processParameter', key: 'tipCollectorDistance', label: 'Tip-Collector Dist.', unitKey: 'tipCollectorDistanceUnit', units: ['cm', 'mm'], required: true, minWidth: 160 },
    { parent: 'processParameter', key: 'spinningDuration', label: 'Spinning Duration', unitKey: 'spinningDurationUnit', units: ['min', 'h', 's'], minWidth: 150 },
    { parent: 'ambientParameter', key: 'temperature', label: 'Temperature', unitKey: 'temperatureUnit', units: ['°C', 'K'], minWidth: 130 },
    { parent: 'ambientParameter', key: 'humidity', label: 'Humidity', unitKey: 'humidityUnit', units: ['%'], minWidth: 110 },
    { parent: 'fiberProperty', key: 'isFormationStable', label: 'Formation Stable?', type: 'bool', minWidth: 120 },
    { parent: 'fiberProperty', key: 'fiberDiameter', label: 'Fiber Diameter', unitKey: 'fiberDiameterUnit', units: ['nm', 'µm'], required: true, minWidth: 140 },
    { parent: 'fiberProperty', key: 'fiberDiameterVariation', label: 'Diameter Variation', unitKey: 'fiberDiameterVariationUnit', unitFrom: 'fiberDiameterUnit', minWidth: 140 },
    { parent: 'fiberProperty', key: 'productWeight', label: 'Product Weight', unitKey: 'productWeightUnit', units: ['µg', 'mg'], minWidth: 140 },
];

/**
 * The run columns of a setup. With separate core/shell solutions, the flow
 * rate is entered per channel (in one shared unit); the run's flow rate is
 * then their sum, worked out on submit.
 */
const runColumnsFor = (group) => {
    const channels = setupChannels(group?.setup);
    if (!channels.length) return RUN_COLUMNS;
    const flow = RUN_COLUMNS.find(c => c.key === 'flowRate');
    const perChannel = channels.map((channel, i) => ({
        ...flow,
        key: `flowRate${channel.charAt(0)}${channel.slice(1).toLowerCase()}`,
        label: `Flow Rate (${CHANNEL_LABELS[channel].toLowerCase()})`,
        // the first channel column carries the unit selector; the others follow it
        ...(i === 0 ? {} : { units: undefined, unitFrom: 'flowRateUnit' }),
    }));
    return RUN_COLUMNS.flatMap(c => (c.key === 'flowRate' ? perChannel : [c]));
};

/** Columns before the Instability column (up to "Formation Stable?"). */
const leadingColumnCount = (columns) => columns.findIndex(c => c.key === 'isFormationStable') + 1;

// Older spellings still found in saved records -> the symbol the unit selectors use.
const UNIT_ALIASES = { ug: 'µg', 'ml/h': 'mL/h', 'ul/min': 'µL/min', um: 'µm', 'mg/cm2/hr': 'mg/cm²/hr' };
const canonicalUnit = (u) => (u && UNIT_ALIASES[u.toLowerCase()]) || u;

const isBlankValue = (v) => v === null || v === undefined || v === '';

/**
 * Sets one value of a run's sub-object. A newly entered fiber diameter is an
 * exact value, so a range or "approximate" flag from a bulk upload is cleared.
 */
const setRunValue = (parentObj, key, value) => {
    const next = { ...parentObj, [key]: value };
    if (key === 'fiberDiameter' && parentObj?.fiberDiameterQualifier && parentObj.fiberDiameterQualifier !== 'EXACT') {
        next.fiberDiameterQualifier = 'EXACT';
        next.fiberDiameterMin = null;
        next.fiberDiameterMax = null;
    }
    return next;
};

/** A pasted spreadsheet cell as a number string ("1,5" -> "1.5", "1,234.5" -> "1234.5", "15 kV" -> "15"), or null. */
const parsePastedNumber = (text) => {
    let t = (text || '').trim();
    if (!t) return '';
    // "150,000" / "1,500" could be thousands or a decimal comma: refuse rather than guess
    if (/^[-+]?\d{1,3}(,\d{3})+(\s|$)/.test(t)) return null;
    t = t.includes(',') && t.includes('.') ? t.replace(/,/g, '') : t.replace(',', '.');
    const m = t.match(/^[-+]?(\d+(\.\d*)?|\.\d+)([eE][-+]?\d+)?/);
    return m ? String(Number(m[0])) : null;
};

const parsePastedBool = (text) => {
    const t = (text || '').trim().toLowerCase();
    if (!t) return null;
    if (['yes', 'y', 'true', '1'].includes(t)) return true;
    if (['no', 'n', 'false', '0'].includes(t)) return false;
    return undefined;
};

/** True on phone-sized screens, where runs are shown as cards instead of a wide table. */
function useIsNarrow(query = '(max-width: 767px)') {
    const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(query).matches);
    React.useEffect(() => {
        const mq = window.matchMedia?.(query);
        if (!mq) return undefined;
        const onChange = (e) => setNarrow(e.matches);
        mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
        return () => (mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange));
    }, [query]);
    return narrow;
}

/**
 * Runs the contributor cannot change: approved runs (published), and rejected runs
 * until the contributor chooses to revise and resubmit them.
 */
const isLockedRun = (r) => r.status === 'APPROVED' || (r.status === 'REJECTED' && !r.resubmit);

const RUN_STATUS = {
    APPROVED: { label: 'Published', tone: 'success', icon: 'bi-shield-lock' },
    PENDING: { label: 'In review', tone: 'neutral', icon: 'bi-hourglass-split' },
    NEEDS_UPDATE: { label: 'Needs update', tone: 'warning', icon: 'bi-exclamation-circle' },
    REJECTED: { label: 'Rejected', tone: 'danger', icon: 'bi-x-circle' },
};

export default function ElectrospinningTable({ onSubmit, initialData, readOnly, imageOnly = false, setImageOnly, onImportMetadata, initialGroups, onGroupsChange,
    onBack, consent = false, onConsentChange, requireConsent = false, isEditMode = false }) {
    const isNarrow = useIsNarrow();
    const confirm = useConfirmation();
    const { success, error, info } = useNotification();

    // Initial State: One Group with One Row (default)
    const [groups, setGroups] = useState([createNewGroup(1)]);

    // Effect to load initial data
    React.useEffect(() => {
        if (initialData && initialData.length > 0) {
            const newGroups = transformBackendDataToState(initialData);
            setGroups(newGroups);
        }
    }, [initialData]);

    // Restoring a saved draft replaces the grid wholesale
    React.useEffect(() => {
        if (initialGroups && initialGroups.length > 0) {
            setGroups(initialGroups);
        }
    }, [initialGroups]);

    // Lets the wrapper autosave a draft
    React.useEffect(() => {
        if (onGroupsChange) onGroupsChange(groups);
    }, [groups]);

    // After a failed submit, empty mandatory cells are outlined
    const [showMissing, setShowMissing] = useState(false);
    const [showProblems, setShowProblems] = useState(false);

    // Modal State
    const [modalState, setModalState] = useState({
        isOpen: false,
        type: null,
        groupId: null,
        rowId: null, // Only for dynamic row data
    });

    const openModal = (type, groupId, rowId = null) => {
        setModalState({ isOpen: true, type, groupId, rowId });
    };

    const closeModal = () => {
        setModalState({ isOpen: false, type: null, groupId: null, rowId: null });
    };

    const blockInvalidChars = (e) => {
        if (["e", "E", "-", "+"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const isFieldInvalid = (val, fieldName) => {
        if (val === null || val === "") return false;
        const numVal = Number(val);
        // Fields strictly allowed to be zero: temperature, humidity
        if (fieldName === 'temperature' || fieldName === 'humidity') return false;
        // All other numerical fields cannot be zero
        return numVal === 0;
    };

    // --- State Management Functions ---

    // Update Setup Data (Polymer, Solvent, etc.)
    /** Updates several setup parts at once (the solution dialog sets polymers, solvents, solution, ...). */
    const updateSetupMany = (groupId, patch) => {
        setGroups(prevGroups => prevGroups.map(g => (g.id !== groupId ? g : { ...g, setup: { ...g.setup, ...patch } })));
        closeModal();
    };

    const updateSetup = (groupId, setupType, data) => {
        setGroups(prevGroups => prevGroups.map(g => {
            if (g.id !== groupId) return g;
            return {
                ...g,
                setup: {
                    ...g.setup,
                    [setupType]: data
                }
            };
        }));
        closeModal();
    };

    // Update Dynamic Row Data (Process Params, etc.)
    const updateRow = (groupId, rowId, parentKey, key, value) => {
        // Enforce Humidity Range 0-100
        if (parentKey === 'ambientParameter' && key === 'humidity' && value !== "") {
            const num = parseFloat(value);
            if (num < 0 || num > 100) return;
        }

        // Enforce Non-Negative Values
        const nonNegativeFields = {
            processParameter: ['voltage', 'flowRate', 'tipCollectorDistance', 'spinningDuration'],
            fiberProperty: ['fiberDiameter', 'fiberDiameterVariation', 'productWeight']
        };

        if (nonNegativeFields[parentKey]?.includes(key) && value !== "") {
            const num = parseFloat(value);
            if (num < 0) return;
        }

        setGroups(prevGroups => prevGroups.map(g => {
            if (g.id !== groupId) return g;
            return {
                ...g,
                rows: g.rows.map(r => {
                    if (r.id !== rowId) return r;
                    // Handle direct assignment or nested object update
                    if (parentKey) {
                        return { ...r, [parentKey]: setRunValue(r[parentKey], key, value) };
                    }
                    return { ...r, [key]: value };
                })
            };
        }));
    };

    // Update Multiple Row Properties Atomically
    const updateRowMultiple = (groupId, rowId, updates) => {
        setGroups(prevGroups => prevGroups.map(g => {
            if (g.id !== groupId) return g;
            return {
                ...g,
                rows: g.rows.map(r => {
                    if (r.id !== rowId) return r;
                    return { ...r, ...updates };
                })
            };
        }));
    };
    const updateFiberImages = (groupId, rowId, images) => {
        setGroups(prevGroups => prevGroups.map(g => {
            if (g.id !== groupId) return g;
            return {
                ...g,
                rows: g.rows.map(r => {
                    if (r.id !== rowId) return r;
                    return { ...r, fiberImages: images };
                })
            };
        }));
        setModalState({ ...modalState, isOpen: false }); // Close modal manually for images if needed
    };

    // Add/Remove Groups
    const addGroup = () => {
        setGroups(prevGroups => {
            const newId = prevGroups.length ? Math.max(...prevGroups.map(g => g.id)) + 1 : 1;
            return [...prevGroups, createNewGroup(newId)];
        });
    };

    const removeGroup = async (groupId) => {
        setGroups(prevGroups => {
            if (prevGroups.length === 1) {
                info("At least one experiment setup is required.");
                return prevGroups;
            }
            return prevGroups; // Placeholder to handle async confirm outside functional update if possible, but actually we need the confirmation result.
        });

        const isConfirmed = await confirm({
            title: "Delete Experiment Setup",
            message: "Are you sure you want to delete this entire setup and all its data rows?",
            confirmLabel: "Delete Setup",
            type: "danger"
        });
        if (isConfirmed) {
            setGroups(prevGroups => prevGroups.filter(g => g.id !== groupId));
        }
    };

    // Original duplicateGroup logic kept but unused if button removed
    const duplicateGroup = (groupId) => {
        success("Setup duplicated with its runs. Change what differs.");
        setGroups(prevGroups => {
            const groupToClone = prevGroups.find(g => g.id === groupId);
            if (!groupToClone) return prevGroups;

            const newId = Math.max(...prevGroups.map(g => g.id)) + 1;
            const newGroup = JSON.parse(JSON.stringify(groupToClone));
            newGroup.id = newId;
            // Reset row IDs
            // The copies are new runs: they must not overwrite the originals' records when editing
            newGroup.rows = newGroup.rows.map((r, idx) => ({ ...r, id: idx + 1, status: 'PENDING', recordId: undefined }));

            return [...prevGroups, newGroup];
        });
    };

    // Add/Remove Rows within a Group
    /** A new empty run that keeps the units chosen for the group's columns. */
    const newRowLike = (g, id) => {
        const row = createNewRow(id);
        const last = g.rows[g.rows.length - 1];
        if (last) {
            runColumnsFor(g).filter(c => c.unitKey).forEach(c => {
                const unit = last[c.parent]?.[c.unitKey];
                if (unit) row[c.parent] = { ...row[c.parent], [c.unitKey]: unit };
            });
        }
        return row;
    };

    const addRowToGroup = (groupId) => {
        setGroups(prevGroups => prevGroups.map(g => {
            if (g.id !== groupId) return g;
            const newRowId = g.rows.length ? Math.max(...g.rows.map(r => r.id)) + 1 : 1;
            return {
                ...g,
                rows: [...g.rows, newRowLike(g, newRowId)]
            };
        }));
    };

    /** Sets a column's unit for every editable run of the group (values are not converted). */
    const setColumnUnit = (groupId, column, unit) => {
        setGroups(prevGroups => prevGroups.map(g => {
            if (g.id !== groupId) return g;
            return {
                ...g,
                rows: g.rows.map(r => {
                    if (isLockedRun(r)) return r;
                    const parent = { ...r[column.parent], [column.unitKey]: unit };
                    // the variation is measured in the diameter's unit
                    runColumnsFor(g).filter(c => c.unitFrom === column.unitKey).forEach(c => { parent[c.unitKey] = unit; });
                    return { ...r, [column.parent]: parent };
                })
            };
        }));
    };

    /** Copies the first filled value of a column into the empty cells below it. */
    const fillDown = (groupId, column) => {
        setGroups(prevGroups => prevGroups.map(g => {
            if (g.id !== groupId) return g;
            const sourceIndex = g.rows.findIndex(r => !isBlankValue(r[column.parent]?.[column.key]));
            if (sourceIndex < 0) return g;
            const value = g.rows[sourceIndex][column.parent][column.key];
            return {
                ...g,
                rows: g.rows.map((r, i) => (i <= sourceIndex || isLockedRun(r)
                    || !isBlankValue(r[column.parent]?.[column.key]))
                    ? r
                    : { ...r, [column.parent]: setRunValue(r[column.parent], column.key, value) })
            };
        }));
    };

    /**
     * Pastes a block copied from a spreadsheet (tab-separated, one line per
     * run) starting at the given cell: columns fill left to right in
     * the setup's run-column order, and runs are added when the block is longer than the table.
     */
    const pasteBlock = (groupId, rowIndex, columnIndex, text) => {
        const lines = text.replace(/\r/g, '').split('\n');
        if (lines.length && lines[lines.length - 1] === '') lines.pop();
        const matrix = lines.map(l => l.split('\t'));
        let pasted = 0;
        let skipped = 0;
        setGroups(groups.map(g => {
            if (g.id !== groupId) return g;
            const rows = [...g.rows];
            matrix.forEach((cells, i) => {
                const target = rowIndex + i;
                if (target >= rows.length) {
                    const nextId = Math.max(0, ...rows.map(r => r.id)) + 1;
                    rows.push(newRowLike({ rows }, nextId));
                }
                if (isLockedRun(rows[target])) { skipped += cells.length; return; }
                let row = rows[target];
                cells.forEach((cell, j) => {
                    const column = runColumnsFor(g)[columnIndex + j];
                    if (!column) return;
                    const value = column.type === 'bool' ? parsePastedBool(cell) : parsePastedNumber(cell);
                    if (value === undefined || value === null) { if (cell.trim()) skipped++; return; }
                    row = { ...row, [column.parent]: setRunValue(row[column.parent], column.key, value) };
                    if (value !== '') pasted++;
                });
                rows[target] = row;
            });
            return { ...g, rows };
        }));
        if (skipped > 0) info(`Pasted ${pasted} value(s); ${skipped} cell(s) were skipped: not a number, ambiguous (like 1,500 - write 1500 or 1.5), or in a locked run.`);
        else success(`Pasted ${pasted} value(s) into ${matrix.length} run(s).`);
    };

    const handleCellPaste = (e, groupId, rowIndex, columnIndex) => {
        const text = e.clipboardData?.getData('text') || '';
        if (!text.includes('\t') && !text.trim().includes('\n')) return; // a single value: normal paste
        e.preventDefault();
        pasteBlock(groupId, rowIndex, columnIndex, text);
    };

    const removeRowFromGroup = (groupId, rowId) => {
        setGroups(prevGroups => prevGroups.map(g => {
            if (g.id !== groupId) return g;
            if (g.rows.length === 1) {
                info("Each setup must have at least one data row.");
                return g;
            }
            return {
                ...g,
                rows: g.rows.filter(r => r.id !== rowId)
            };
        }));
    };

    const copyRow = (groupId, rowId) => {
        setGroups(prevGroups => prevGroups.map(g => {
            if (g.id !== groupId) return g;
            const rowToCopy = g.rows.find(r => r.id === rowId);
            if (!rowToCopy) return g;

            const newRowId = g.rows.length ? Math.max(...g.rows.map(r => r.id)) + 1 : 1;
            const newRow = JSON.parse(JSON.stringify(rowToCopy));
            newRow.id = newRowId;
            newRow.status = 'PENDING';
            // A copy is a new record: it must not overwrite the original on update,
            // and uploaded images belong to exactly one record.
            delete newRow.recordId;
            delete newRow.resubmit;
            delete newRow.reviewNote;
            delete newRow.reviewedAt;
            delete newRow.openIssues;
            newRow.fiberImages = [];

            return {
                ...g,
                rows: [...g.rows, newRow]
            }
        }));
    };


    // --- Validation & Submission ---

    /**
     * Checks the whole grid and reports every problem with where it is
     * ("Setup 2, run 3: Flow Rate is required") instead of stopping at the first.
     * Mirrors the backend rules; the backend re-checks everything on submit.
     */
    /** Every problem in the grid, with where it is ("Setup 2, run 3: Flow Rate is required"). */
    const collectProblems = () => {
        const problems = [];
        if (imageOnly) {
            const totalImages = groups.reduce((n, g) => n + g.rows.reduce((m, r) => m + (r.fiberImages?.length || 0), 0), 0);
            if (totalImages === 0) problems.push("Please upload at least one image to submit an image dataset.");
        }

        groups.forEach((group, gIndex) => {
            const where = groups.length > 1 ? `Setup ${gIndex + 1}` : 'Setup';
            const s = group.setup;
            if (!imageOnly) {
                const noSolution = !s.polymerProperty?.polymerComponents?.length && !s.solventProperty?.solventComponents?.length
                    && !s.solutions?.length;
                if (noSolution) problems.push(`${where}: describe the polymer solution.`);
                if (!noSolution && !s.polymerProperty?.polymerComponents?.length) problems.push(`${where}: add at least one polymer.`);
                if (!noSolution && !s.solventProperty?.solventComponents?.length) problems.push(`${where}: add at least one solvent.`);
                const channels = setupChannels(s);
                const needleChannels = channelsForNeedle(s.needleProperty?.needleType);
                if (channels.length && channels.join() !== needleChannels.join()) {
                    problems.push(`${where}: the polymer solution describes ${channels.map(c => CHANNEL_LABELS[c].toLowerCase()).join(' and ')} solutions, `
                        + `which does not match the ${s.needleProperty?.needleType || 'chosen'} needle. Open the polymer solution to update it.`);
                }
                if (s.solutions?.length) {
                    if (s.solutions.some(f => (f.stocks || []).some(st => !st.concentration && (st.polymers || []).length))) {
                        problems.push(`${where}: each solution with a polymer needs its concentration.`);
                    }
                } else if (!noSolution && !s.solutionProperty?.concentration) {
                    problems.push(`${where}: the solution concentration is required.`);
                }
                if (!s.collectorProperty?.collectorType) problems.push(`${where}: choose a collector.`);
                if (!s.needleProperty?.needleType) problems.push(`${where}: choose a needle.`);
            }

            group.rows.forEach((row, rIndex) => {
                if (isLockedRun(row)) return;
                const at = `${where}, run ${rIndex + 1}`;
                runColumnsFor(group).filter(c => c.type !== 'bool').forEach(c => {
                    const raw = row[c.parent]?.[c.key];
                    if (isBlankValue(raw)) {
                        if (c.required && !imageOnly) problems.push(`${at}: ${c.label} is required.`);
                        return;
                    }
                    const value = Number(raw);
                    if (Number.isNaN(value)) {
                        problems.push(`${at}: ${c.label} is not a number.`);
                    } else if (c.key === 'humidity') {
                        if (value < 0 || value > 100) problems.push(`${at}: Humidity must be between 0 and 100.`);
                    } else if (c.key === 'temperature') {
                        const unit = row[c.parent]?.[c.unitKey];
                        if (value < (unit === 'K' ? 0 : -273.15)) problems.push(`${at}: Temperature is below absolute zero.`);
                    } else if (value <= 0) {
                        problems.push(`${at}: ${c.label} must be greater than zero.`);
                    }
                });
            });
        });

        return problems;
    };

    const validateGroups = () => {
        const problems = collectProblems();
        if (requireConsent && !consent) {
            problems.push("Please accept the Terms of Use and License Agreement to submit.");
        }
        if (problems.length > 0) {
            setShowMissing(true);
            const dataProblems = problems.length - (requireConsent && !consent ? 1 : 0);
            if (dataProblems === 0) {
                error("Please accept the Terms of Use and License Agreement to submit.");
            } else {
                setShowProblems(true);
                error(`${dataProblems} item${dataProblems === 1 ? ' needs' : 's need'} your attention; they are listed above the Submit button and outlined in red.`);
                // Bring the first one into view once the outlines are drawn
                setTimeout(() => document.querySelector('.cx-tile.is-missing, .cx-cell.is-invalid')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
            }
            return false;
        }
        setShowMissing(false);
        return true;
    };

    const handleSubmit = () => {
        if (!validateGroups()) return;

        // Flatten Data
        const flatData = groups.flatMap(group => {
            return group.rows.map(row => {
                // Create a copy without the frontend-specific ID
                const { id, ...rowData } = row;
                return {
                    recordId: row.recordId, // Preserve backend ID
                    resubmit: row.resubmit || undefined,
                    // per-run provenance (e.g. from a bulk upload) survives an edit
                    dataOrigin: row.dataOrigin,
                    extractionMethod: row.extractionMethod,
                    sourceLocation: row.sourceLocation,
                    status: row.status,
                    // Spread Setup
                    polymerProperty: group.setup.polymerProperty,
                    solventProperty: group.setup.solventProperty,
                    solutionProperty: group.setup.solutionProperty,
                    collectorProperty: group.setup.collectorProperty,
                    needleProperty: group.setup.needleProperty,
                    additives: group.setup.additives || [],
                    solutions: group.setup.solutions || null,
                    // Spread Row Data
                    processParameter: row.processParameter,
                    ambientParameter: row.ambientParameter,
                    fiberProperty: row.fiberProperty,
                    mechanicalProperty: row.mechanicalProperty,
                    functionalProperty: row.functionalProperty,
                    applicationProperty: row.applicationProperty,
                    fiberImages: row.fiberImages
                };
            });
        });

        if (onSubmit) {
            onSubmit(flatData);
        }
    };

    const handleExcelUpload = async (file) => {
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        try {
            // Using ApiClient.request directly to pass FormData with correct body
            const response = await ApiClient.request('/data/parse-excel', {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                const data = await response.json();
                if (Array.isArray(data) && data.length > 0) {
                    const imported = transformBackendDataToState(data);
                    // Append to what's already entered; only the untouched starter group is replaced.
                    setGroups(prev => {
                        const kept = prev.filter(g => !isGroupEmpty(g));
                        return [...kept, ...imported].map((g, i) => ({ ...g, id: i + 1 }));
                    });
                    success(`Imported ${data.length} run${data.length === 1 ? '' : 's'} from Excel.`);
                    if (onImportMetadata) {
                        onImportMetadata(data.map(item => item.researchMetadata).filter(Boolean));
                    }
                } else {
                    info("No valid data found in Excel file.");
                }
            } else {
                let message = "Failed to parse Excel file.";
                try {
                    const body = await response.json();
                    if (body?.message) message = body.message;
                } catch (_) { /* non-JSON error body */ }
                error(message);
            }
        } catch (err) {
            console.error(err);
            error("Error uploading file.");
        }

        // Reset input
        document.getElementById('excel-upload-input').value = '';
    };

    const transformBackendDataToState = (backendData) => {
        // Group by Content Configuration equality
        // Configuration fields: polymerProperty, solventProperty, solutionProperty, collectorProperty, needleProperty

        const groups = [];

        backendData.forEach(item => {
            const setup = {
                polymerProperty: item.polymerProperty,
                solventProperty: item.solventProperty,
                solutionProperty: item.solutionProperty,
                collectorProperty: item.collectorProperty,
                needleProperty: item.needleProperty,
                additives: item.additives || [],
                solutions: item.solutions || null,
            };

            // Find existing group with same setup
            let foundGroup = groups.find(g => isSetupEqual(g.setup, setup));

            if (!foundGroup) {
                foundGroup = {
                    id: groups.length + 1,
                    setup: setup,
                    rows: []
                };
                groups.push(foundGroup);
            }

            // Create row
            const row = {
                id: foundGroup.rows.length + 1,
                recordId: item.recordId,
                status: item.status || 'PENDING',
                reviewNote: item.reviewNote,
                reviewedAt: item.reviewedAt,
                openIssues: item.openIssues,
                dataOrigin: item.dataOrigin,
                extractionMethod: item.extractionMethod,
                sourceLocation: item.sourceLocation,
                processParameter: { ...item.processParameter, instability: item.processParameter?.instability || [] } || { instability: [] },
                ambientParameter: item.ambientParameter || {},
                fiberProperty: item.fiberProperty || { fiberMorphology: [] },
                mechanicalProperty: item.mechanicalProperty || {},
                functionalProperty: item.functionalProperty || {},
                applicationProperty: item.applicationProperty || {},
                fiberImages: item.fiberImages || [],
            };

            foundGroup.rows.push(row);
        });

        // Ensure groups have defaults if missing (though backend usually sends them)
        return groups.map(g => ({
            ...g,
            setup: {
                polymerProperty: g.setup.polymerProperty || { polymerComponents: [] },
                solventProperty: g.setup.solventProperty || { solventComponents: [] },
                solutionProperty: g.setup.solutionProperty || {},
                collectorProperty: g.setup.collectorProperty || {},
                needleProperty: g.setup.needleProperty || {},
                additives: g.setup.additives || [],
                solutions: g.setup.solutions || null,
            }
        }));
    };

    // True for a group nobody has filled in yet (the starter group on a fresh form)
    const isGroupEmpty = (group) => {
        const s = group.setup;
        const setupEmpty = !s.polymerProperty?.polymerComponents?.length &&
            !s.solventProperty?.solventComponents?.length &&
            !s.solutionProperty?.concentration &&
            !s.collectorProperty?.collectorType &&
            !s.needleProperty?.needleType;
        const rowsEmpty = group.rows.every(r =>
            !r.recordId &&
            !r.fiberImages?.length &&
            [r.processParameter?.voltage, r.processParameter?.flowRate, r.processParameter?.tipCollectorDistance,
                r.fiberProperty?.fiberDiameter].every(v => v === null || v === undefined || v === ""));
        return setupEmpty && rowsEmpty;
    };

    // Deep comparison helper for setup fields
    const isSetupEqual = (setup1, setup2) => {
        // Simple JSON stringify comparison - works if key order is consistent.
        // For safer compare, we could implement field by field. 
        // Given DTO structure stability, this is usually acceptable for this purpose.
        // We can explicitly comparing specific main objects to be safer against ordering of root keys.
        return JSON.stringify(setup1.polymerProperty) === JSON.stringify(setup2.polymerProperty) &&
            JSON.stringify(setup1.solventProperty) === JSON.stringify(setup2.solventProperty) &&
            JSON.stringify(setup1.solutionProperty) === JSON.stringify(setup2.solutionProperty) &&
            JSON.stringify(setup1.collectorProperty) === JSON.stringify(setup2.collectorProperty) &&
            JSON.stringify(setup1.needleProperty) === JSON.stringify(setup2.needleProperty) &&
            JSON.stringify(setup1.additives || []) === JSON.stringify(setup2.additives || []) &&
            JSON.stringify(setup1.solutions || null) === JSON.stringify(setup2.solutions || null);
    };

    const navigate = useNavigate();

    // --- Helpers for Modals ---
    // Extract current data for modal based on state
    const getCurrentModalData = () => {
        if (!modalState.isOpen) return null;
        const group = groups.find(g => g.id === modalState.groupId);
        if (!group) return null;

        if (modalState.rowId) {
            // It's a row modal (Fiber Morphology, Images)
            const row = group.rows.find(r => r.id === modalState.rowId);
            if (!row) return null;

            if (modalState.type === 'fiberMorphology') {
                return {
                    data: row.fiberProperty.fiberMorphology,
                    save: (d) => updateRow(group.id, row.id, 'fiberProperty', 'fiberMorphology', d)
                };
            }
            if (modalState.type === 'uploadImage') {
                return {
                    data: row.fiberImages,
                    save: (d) => updateFiberImages(group.id, row.id, d.fiberImages)
                };
            }
            if (modalState.type === 'processInstability') {
                return {
                    data: row.processParameter.instability,
                    save: (d) => updateRow(group.id, row.id, 'processParameter', 'instability', d)
                };
            }
            if (modalState.type === 'propertyAnalysis') {
                return {
                    data: {
                        mechanical: row.mechanicalProperty,
                        functional: row.functionalProperty,
                        application: row.applicationProperty
                    },
                    save: (d) => {
                        updateRowMultiple(group.id, row.id, {
                            mechanicalProperty: d.mechanical,
                            functionalProperty: d.functional,
                            applicationProperty: d.application
                        });
                    }
                };
            }
        } else {
            // It's a setup modal
            if (modalState.type === 'solutionPrep') {
                return {
                    data: group.setup,
                    save: (patch) => updateSetupMany(group.id, patch)
                };
            }
            if (modalState.type === 'collector') {
                return {
                    data: group.setup.collectorProperty,
                    save: (d) => updateSetup(group.id, 'collectorProperty', d)
                }
            }
            if (modalState.type === 'needle') {
                return {
                    data: group.setup.needleProperty,
                    save: (d) => updateSetup(group.id, 'needleProperty', d)
                }
            }
        }
        return null;
    };

    const currentModal = getCurrentModalData();

    // Transform helpers for FiberMorphology
    const transformSavedData = (savedArray) => {
        if (!Array.isArray(savedArray)) return {};
        // Check if savedArray is already transformed key-value, if so just return it
        if (!Array.isArray(savedArray)) return savedArray;

        return savedArray.reduce((acc, { category, abbreviation }) => {
            if (!acc[category]) acc[category] = [];
            acc[category].push(abbreviation);
            return acc;
        }, {});
    }
    const convertSelectionToFiberMorphologyArray = (selected) => {
        if (!selected) return [];
        return Object.entries(selected).flatMap(([category, abbreviations]) => {
            return abbreviations.map((abbreviation) => ({
                category,
                abbreviation,
                label: "",
            }));
        });
    };

    function groupImagesByType(fiberImages) {
        if (!fiberImages || !Array.isArray(fiberImages)) return {};
        return fiberImages.reduce((acc, img) => {
            const type = img.imageType || "Unknown";
            if (!acc[type]) acc[type] = [];
            acc[type].push(img);
            return acc;
        }, {});
    }

    const hasProperties = (row) => {
        const check = (obj) => {
            if (!obj) return false;
            return Object.entries(obj).some(([key, value]) => {
                if (key.toLowerCase().endsWith('unit')) return false;
                return value !== null && value !== "" && value !== undefined;
            });
        };
        return check(row.mechanicalProperty) || check(row.functionalProperty) || check(row.applicationProperty);
    };


    // --- Run table cells ---

    const unitOf = (group, column) => {
        const key = column.unitFrom || column.unitKey;
        const source = RUN_COLUMNS.find(c => c.unitKey === key) || column;
        return canonicalUnit(group.rows[0]?.[source.parent]?.[key]) || source.units?.[0] || '';
    };

    const renderUnitPicker = (group, column, locked) => {
        const unit = column.type === 'bool' ? null : unitOf(group, column);
        if (!unit) return null;
        const canPickUnit = !readOnly && !locked && column.units?.length > 1;
        return canPickUnit ? (
            <select className="cx-select" value={column.units.includes(unit) ? unit : ''}
                onChange={e => setColumnUnit(group.id, column, e.target.value)}
                aria-label={`${column.label} unit`}>
                {!column.units.includes(unit) && <option value="" disabled>{unit}</option>}
                {column.units.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
        ) : <span>{unit}</span>;
    };

    const renderRunHeader = (group, column, locked) => (
        <th key={column.key} scope="col" style={{ minWidth: `${column.minWidth - 20}px` }}>
            <div className="cx-colhead">
                <span>{column.label}{column.required && !imageOnly && <span className="cx-req" aria-label="required">*</span>}</span>
                {!readOnly && group.rows.length > 1 && (
                    <button type="button" className="cx-iconbtn" style={{ width: '1.5rem', height: '1.5rem' }}
                        title="Fill down: copy the first value into the empty cells below"
                        aria-label={`Fill down ${column.label}`}
                        onClick={() => fillDown(group.id, column)}>
                        <i className="bi bi-arrow-down" aria-hidden="true"></i>
                    </button>
                )}
            </div>
            <div className="cx-unit">{renderUnitPicker(group, column, locked)}</div>
        </th>
    );

    /** The input for one value of a run (used by the table and by the run cards). */
    const renderRunInput = (group, row, rIndex, column, cIndex, rowLocked) => {
        const value = row[column.parent]?.[column.key];
        if (column.type === 'bool') {
            return (
                <select className="cx-cell"
                    value={value === true ? "yes" : value === false ? "no" : ""}
                    onChange={e => updateRow(group.id, row.id, column.parent, column.key, e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null)}
                    disabled={rowLocked}
                    aria-label={`${column.label}, run ${rIndex + 1}`}>
                    <option value="">—</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                </select>
            );
        }
        const missing = showMissing && column.required && !imageOnly && isBlankValue(value) && !rowLocked;
        const invalid = isFieldInvalid(value, column.key) || missing;
        return (
            <>
                <input type="number" inputMode="decimal"
                    className={`cx-cell ${invalid ? 'is-invalid' : ''}`}
                    min={column.key === 'temperature' ? undefined : "0"}
                    max={column.key === 'humidity' ? "100" : undefined}
                    step="any"
                    value={value ?? ""}
                    onChange={e => updateRow(group.id, row.id, column.parent, column.key, e.target.value)}
                    onPaste={e => !rowLocked && handleCellPaste(e, group.id, rIndex, cIndex)}
                    disabled={rowLocked}
                    onKeyDown={column.key === 'temperature' ? undefined : blockInvalidChars}
                    aria-label={`${column.label}, run ${rIndex + 1}`}
                    aria-invalid={invalid || undefined}
                />
                {column.key === 'fiberDiameter' && row.fiberProperty?.fiberDiameterQualifier === 'RANGE' && (
                    <div className="cx-cellnote" title="Reported as a range; the midpoint is shown. Typing a new value makes it exact.">
                        range {row.fiberProperty.fiberDiameterMin}–{row.fiberProperty.fiberDiameterMax}
                    </div>
                )}
                {column.key === 'fiberDiameter' && row.fiberProperty?.fiberDiameterQualifier === 'APPROXIMATE' && (
                    <div className="cx-cellnote" title="Reported as approximate. Typing a new value makes it exact.">approx.</div>
                )}
            </>
        );
    };

    const renderRunCell = (group, row, rIndex, column, cIndex, rowLocked) => (
        <td key={column.key}>{renderRunInput(group, row, rIndex, column, cIndex, rowLocked)}</td>
    );

    /** Instability, morphology, properties and images of a run: a chip that opens its dialog. */
    const EXTRAS = [
        {
            key: 'instability', label: 'Instability', modal: 'processInstability', icon: 'bi-activity',
            count: (row) => row.processParameter?.instability?.length || 0,
            summary: (row) => <InstabilitySummary instabilities={row.processParameter.instability} />,
        },
        {
            key: 'morphology', label: 'Morphology', modal: 'fiberMorphology', icon: 'bi-bounding-box',
            count: (row) => row.fiberProperty?.fiberMorphology?.length || 0,
            summary: (row) => <FiberMorphologySummary fiberMorphology={row.fiberProperty.fiberMorphology} />,
        },
        {
            key: 'properties', label: 'Properties', modal: 'propertyAnalysis', icon: 'bi-bar-chart',
            count: (row) => (hasProperties(row) ? 1 : 0),
            summary: (row) => <PropertySummary mechanical={row.mechanicalProperty} functional={row.functionalProperty} application={row.applicationProperty} />,
        },
        {
            key: 'images', label: 'Images', modal: 'uploadImage', icon: 'bi-image',
            count: (row) => row.fiberImages?.length || 0,
            summary: (row) => <FiberImagesSummary fiberImages={row.fiberImages} />,
        },
    ];

    const renderExtra = (extra, group, row, rIndex, rowLocked) => {
        const count = extra.count(row);
        const chip = (
            <button type="button" className={`cx-chipbtn ${count ? 'is-set' : ''}`} disabled={rowLocked}
                onClick={() => openModal(extra.modal, group.id, row.id)}
                aria-label={`${extra.label} of run ${rIndex + 1}${count ? ` (${extra.key === 'properties' ? 'added' : count})` : ''}`}>
                <i className={`bi ${count ? 'bi-check2' : extra.icon}`} aria-hidden="true"></i>
                {count ? (extra.key === 'properties' ? 'Added' : count) : 'Add'}
            </button>
        );
        if (!count) return chip;
        return (
            <OverlayTrigger trigger={['hover', 'focus']} placement="left"
                overlay={<Popover id={`${extra.key}-popover-${group.id}-${row.id}`}><Popover.Body className="p-3">{extra.summary(row)}</Popover.Body></Popover>}>
                {chip}
            </OverlayTrigger>
        );
    };

    const renderRowActions = (group, row, rIndex, rowLocked) => (
        <>
            <button type="button" className="cx-iconbtn" title="Duplicate run" aria-label={`Duplicate run ${rIndex + 1}`}
                onClick={() => copyRow(group.id, row.id)} disabled={readOnly}>
                <i className="bi bi-copy" aria-hidden="true"></i>
            </button>
            <button type="button" className="cx-iconbtn cx-iconbtn--danger" title="Delete run" aria-label={`Delete run ${rIndex + 1}`}
                onClick={() => removeRowFromGroup(group.id, row.id)} disabled={readOnly || row.status === 'APPROVED' || group.rows.length === 1}>
                <i className="bi bi-trash3" aria-hidden="true"></i>
            </button>
        </>
    );

    const problems = collectProblems();
    const runCount = groups.reduce((n, g) => n + g.rows.length, 0);
    const [problemsOpen, setProblemsOpen] = [showProblems, setShowProblems];

    const TILES = [
        {
            key: 'solution', title: 'Polymer solution', icon: 'bi-droplet-half', modal: 'solutionPrep', cta: 'Describe the solution',
            isSet: (s) => !!(s.polymerProperty?.polymerComponents?.length || s.solutions?.length),
            render: (g) => <SolutionPreparationSummary setup={g.setup} />,
        },
        {
            key: 'collector', title: 'Collector', icon: 'bi-bullseye', modal: 'collector', cta: 'Choose the collector',
            isSet: (s) => !!s.collectorProperty?.collectorType,
            render: (g) => <CollectorSummary collector={g.setup.collectorProperty} />,
        },
        {
            key: 'needle', title: 'Needle', icon: 'bi-vector-pen', modal: 'needle', cta: 'Choose the needle',
            isSet: (s) => !!s.needleProperty?.needleType,
            render: (g) => <NeedleSummary needleProperty={g.setup.needleProperty} />,
        },
    ];

    const renderTile = (tile, group, setupLocked) => {
        const done = tile.isSet(group.setup);
        const missing = !done && showMissing && !imageOnly;
        const open = () => !setupLocked && openModal(tile.modal, group.id);
        return (
            <div key={tile.key} role="button" tabIndex={setupLocked ? -1 : 0} aria-disabled={setupLocked || undefined}
                className={`cx-tile ${done ? 'is-done' : 'is-empty'} ${missing ? 'is-missing' : ''} ${setupLocked ? 'is-locked' : ''}`}
                onClick={open} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
                aria-label={`${tile.title}: ${done ? 'edit' : tile.cta.toLowerCase()}`}>
                <span className="cx-tile__icon" aria-hidden="true"><i className={`bi ${done ? 'bi-check2' : tile.icon}`}></i></span>
                <div className="cx-tile__body">
                    <div className="cx-tile__title">{tile.title}{!imageOnly && <span className="cx-req" aria-hidden="true">*</span>}</div>
                    {done
                        ? <div className="cx-tile__content">{tile.render(group)}</div>
                        : <span className="cx-tile__cta"><i className="bi bi-plus-lg me-1" aria-hidden="true"></i>{tile.cta}</span>}
                    {missing && <div className="cx-help cx-help--error mt-1">Required</div>}
                </div>
                {!setupLocked && done && <i className="bi bi-pencil cx-tile__edit" aria-hidden="true"></i>}
                {setupLocked && <i className="bi bi-lock cx-tile__edit" aria-hidden="true"></i>}
            </div>
        );
    };

    const renderRunsTable = (group, columns, lead, hasApprovedRuns) => (
        <div className="cx-tablewrap">
            <table className="cx-table">
                <thead>
                    <tr>
                        <th className="cx-col-index" scope="col">Run</th>
                        {columns.slice(0, lead).map(c => renderRunHeader(group, c, hasApprovedRuns))}
                        <th scope="col">Instability</th>
                        {columns.slice(lead).map(c => renderRunHeader(group, c, hasApprovedRuns))}
                        <th scope="col">Morphology</th>
                        <th scope="col">Properties</th>
                        <th scope="col">Images</th>
                        <th className="cx-col-actions" scope="col"><span className="visually-hidden">Actions</span></th>
                    </tr>
                </thead>
                <tbody>
                    {group.rows.map((row, rIndex) => {
                        const rowLocked = readOnly || isLockedRun(row);
                        return (
                            <tr key={row.id}>
                                <td className="cx-col-index">
                                    {rIndex + 1}
                                    {renderRunStatus(group, row, rIndex)}
                                </td>
                                {columns.slice(0, lead).map((c, cIndex) => renderRunCell(group, row, rIndex, c, cIndex, rowLocked))}
                                <td>{renderExtra(EXTRAS[0], group, row, rIndex, rowLocked)}</td>
                                {columns.slice(lead).map((c, cIndex) => renderRunCell(group, row, rIndex, c, cIndex + lead, rowLocked))}
                                <td>{renderExtra(EXTRAS[1], group, row, rIndex, rowLocked)}</td>
                                <td>{renderExtra(EXTRAS[2], group, row, rIndex, rowLocked)}</td>
                                <td>{renderExtra(EXTRAS[3], group, row, rIndex, rowLocked)}</td>
                                <td className="cx-col-actions">{renderRowActions(group, row, rIndex, rowLocked)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    const renderRunCards = (group, columns, hasApprovedRuns) => (
        <>
            {columns.some(c => c.units?.length > 1) && (
                <div className="cx-unitbar" aria-label="Units">
                    {columns.filter(c => c.units?.length > 1).map(c => (
                        <label key={c.key}>{c.label} {renderUnitPicker(group, c, hasApprovedRuns)}</label>
                    ))}
                </div>
            )}
            {group.rows.map((row, rIndex) => {
                const rowLocked = readOnly || isLockedRun(row);
                return (
                    <div key={row.id} className="cx-runcard">
                        <div className="cx-runcard__head">
                            <span className="d-inline-flex align-items-center gap-2">Run {rIndex + 1}{renderRunStatus(group, row, rIndex)}</span>
                            <span>{renderRowActions(group, row, rIndex, rowLocked)}</span>
                        </div>
                        <div className="cx-runcard__grid">
                            {columns.map((c, cIndex) => {
                                const unit = c.type === 'bool' ? null : unitOf(group, c);
                                return (
                                    <div key={c.key}>
                                        <label>{c.label}{c.required && !imageOnly && <span className="cx-req">*</span>}{unit && <span className="cx-optional"> ({unit})</span>}</label>
                                        {renderRunInput(group, row, rIndex, c, cIndex, rowLocked)}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="cx-runcard__extras">
                            {EXTRAS.map(x => (
                                <span key={x.key} className="d-inline-flex align-items-center gap-1">
                                    <span className="cx-hint">{x.label}</span>{renderExtra(x, group, row, rIndex, rowLocked)}
                                </span>
                            ))}
                        </div>
                    </div>
                );
            })}
        </>
    );

    /** Unlocks a rejected run; saving sends it back to review with the changes. */
    const reviseRun = (groupId, rowId) => {
        setGroups(prev => prev.map(g => g.id !== groupId ? g : {
            ...g, rows: g.rows.map(r => r.id === rowId ? { ...r, resubmit: true } : r),
        }));
        info("The run is unlocked. Saving sends it back to review.");
    };

    const renderRunStatus = (group, row) => {
        if (!isEditMode || !row.recordId) return null;
        const meta = RUN_STATUS[row.status] || RUN_STATUS.PENDING;
        const resubmitting = row.status === 'REJECTED' && row.resubmit;
        const label = resubmitting ? 'Resubmitting' : meta.label;
        const tip = [row.reviewNote && `Reviewer: ${row.reviewNote}`, ...(row.openIssues || [])].filter(Boolean).join('\n');
        return (
            <span className={`cx-runstatus cx-runstatus--${resubmitting ? 'neutral' : meta.tone}`} title={tip || label}>
                <i className={`bi ${resubmitting ? 'bi-arrow-repeat' : meta.icon}`} aria-hidden="true"></i>
                <span className="cx-runstatus__label">{label}</span>
                {row.reviewNote && <i className="bi bi-chat-left-text" aria-label="Has a reviewer note"></i>}
            </span>
        );
    };

    // Runs with a reviewer note, a rejection or open issues, listed where the contributor starts
    const feedback = isEditMode ? groups.flatMap((g, gIndex) => g.rows.map((r, rIndex) => ({ g, gIndex, r, rIndex })))
        .filter(({ r }) => r.recordId && (r.reviewNote || r.status === 'REJECTED' || r.openIssues?.length)) : [];

    const renderFeedback = () => feedback.length > 0 && (
        <section className="cx-card cx-feedback" aria-labelledby="feedback-title">
            <div className="cx-card__head">
                <div>
                    <h2 id="feedback-title"><i className="bi bi-chat-left-text me-2" aria-hidden="true"></i>Review feedback</h2>
                    <p>What the moderators said and what is still missing. Fix the runs below, then save.</p>
                </div>
            </div>
            <ul className="cx-feedback__list">
                {feedback.map(({ g, gIndex, r, rIndex }) => {
                    const meta = RUN_STATUS[r.status] || RUN_STATUS.PENDING;
                    return (
                        <li key={`${g.id}-${r.id}`}>
                            <div className="cx-feedback__where">
                                <strong>Setup {gIndex + 1}, run {rIndex + 1}</strong>
                                <span className={`cx-runstatus cx-runstatus--${meta.tone}`}><i className={`bi ${meta.icon}`} aria-hidden="true"></i><span className="cx-runstatus__label">{meta.label}</span></span>
                            </div>
                            <div className="cx-feedback__body">
                                {r.reviewNote && (
                                    <blockquote className="cx-feedback__note">
                                        {r.reviewNote}
                                        {r.reviewedAt && <footer>Moderator, {new Date(r.reviewedAt).toLocaleDateString()}</footer>}
                                    </blockquote>
                                )}
                                {r.openIssues?.length > 0 && (
                                    <ul className="cx-feedback__issues">{r.openIssues.map(m => <li key={m}>{m}</li>)}</ul>
                                )}
                                {r.status === 'REJECTED' && !r.reviewNote && <p className="cx-hint mb-0">No reason was given. You can revise the run or ask us via the contact page.</p>}
                            </div>
                            {r.status === 'REJECTED' && !readOnly && (
                                r.resubmit
                                    ? <span className="cx-hint"><i className="bi bi-arrow-repeat me-1" aria-hidden="true"></i>Will be resubmitted on save</span>
                                    : <button type="button" className="cx-btn cx-btn--secondary cx-btn--sm" onClick={() => reviseRun(g.id, r.id)}>Revise and resubmit</button>
                            )}
                        </li>
                    );
                })}
            </ul>
        </section>
    );

    const handleCancel = async () => {
        const ok = await confirm({
            title: isEditMode ? "Leave without saving?" : "Leave the contribution?",
            message: isEditMode ? "Your changes to this contribution will not be saved."
                : "Your draft stays saved in this browser, so you can continue later.",
            confirmLabel: "Leave",
            type: "warning",
        });
        if (ok) navigate(isEditMode ? '/my-submissions' : '/');
    };

    const submitLabel = readOnly ? 'Update metadata' : isEditMode ? 'Save changes' : 'Submit for review';

    return (
        <div className="cx-data">
            <input type="file" accept=".xlsx" hidden id="excel-upload-input"
                onChange={(e) => handleExcelUpload(e.target.files[0])} />

            {renderFeedback()}

            <section className="cx-card" aria-labelledby="data-title">
                <div className="cx-card__head">
                    <div>
                        <h2 id="data-title">Experimental data</h2>
                        <p>A <strong>setup</strong> holds what stays the same (solution, collector, needle); each <strong>run</strong> below it holds what changes.</p>
                    </div>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                        <div className="cx-segmented" role="radiogroup" aria-label="What are you contributing?">
                            <button type="button" role="radio" aria-checked={!imageOnly} onClick={() => setImageOnly(false)} disabled={readOnly}>Measurements</button>
                            <button type="button" role="radio" aria-checked={imageOnly} onClick={() => setImageOnly(true)} disabled={readOnly}>Fiber images only</button>
                        </div>
                        <button type="button" className="cx-btn cx-btn--secondary cx-btn--sm" disabled={readOnly}
                            onClick={() => document.getElementById('excel-upload-input').click()}
                            title="Fill this form from an .xlsx file in the dataset export layout">
                            <i className="bi bi-file-earmark-spreadsheet" aria-hidden="true"></i> Import from Excel
                        </button>
                    </div>
                </div>
                {imageOnly && (
                    <div className="cx-card__body py-3">
                        <div className="cx-notice cx-notice--info mb-0">
                            <span><i className="bi bi-image me-2"></i>Only fiber images are required: add them to each run in the <strong>Images</strong> column. Everything else is optional.</span>
                        </div>
                    </div>
                )}
            </section>

            {groups.map((group, index) => {
                // A setup with an approved run is locked, and cannot be deleted.
                const hasApprovedRuns = group.rows.some(r => r.status === 'APPROVED');
                const hasLockedRejected = group.rows.some(r => r.status === 'REJECTED' && !r.resubmit);
                const setupLocked = readOnly || hasApprovedRuns || hasLockedRejected;
                const columns = runColumnsFor(group);
                const lead = leadingColumnCount(columns);
                return (
                    <section key={group.id} className="cx-card" aria-labelledby={`setup-title-${group.id}`}>
                        <div className="cx-card__head">
                            <div className="cx-setup__label">
                                <h3 id={`setup-title-${group.id}`}>Setup {index + 1}</h3>
                                {hasApprovedRuns && <span className="cx-badge cx-badge--success"><i className="bi bi-shield-lock" aria-hidden="true"></i> Contains approved runs</span>}
                                {!hasApprovedRuns && hasLockedRejected && <span className="cx-badge cx-badge--danger" title="Revise the rejected runs to change this setup"><i className="bi bi-lock" aria-hidden="true"></i> Contains rejected runs</span>}
                            </div>
                            {!readOnly && (
                                <div className="d-flex gap-1">
                                    <button type="button" className="cx-btn cx-btn--ghost cx-btn--sm" onClick={() => duplicateGroup(group.id)}>
                                        <i className="bi bi-copy" aria-hidden="true"></i> Duplicate
                                    </button>
                                    {!setupLocked && groups.length > 1 && (
                                        <button type="button" className="cx-btn cx-btn--danger-ghost cx-btn--sm" onClick={() => removeGroup(group.id)}>
                                            <i className="bi bi-trash3" aria-hidden="true"></i> Remove
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="cx-card__body">
                            <div className="cx-tiles">
                                {TILES.map(tile => renderTile(tile, group, setupLocked))}
                            </div>

                            <div className="cx-runs-head">
                                <h4>Runs <span className="cx-badge">{group.rows.length}</span></h4>
                                <div className="d-flex flex-wrap align-items-center gap-3">
                                    {!readOnly && !isNarrow && (
                                        <details className="cx-hint">
                                            <summary><i className="bi bi-clipboard" aria-hidden="true"></i> Paste from a spreadsheet</summary>
                                            <p>Copy a block of cells in Excel and paste it into a run's cell: values fill the columns to the right and new runs are added as needed. <code>1,5</code> and <code>15 kV</code> are read as numbers. Pick each column's unit in its header; <i className="bi bi-arrow-down"></i> copies the first value down.</p>
                                        </details>
                                    )}
                                    {!readOnly && (
                                        <button type="button" className="cx-btn cx-btn--secondary cx-btn--sm" onClick={() => addRowToGroup(group.id)}>
                                            <i className="bi bi-plus-lg" aria-hidden="true"></i> Add run
                                        </button>
                                    )}
                                </div>
                            </div>
                            {isNarrow ? renderRunCards(group, columns, hasApprovedRuns) : renderRunsTable(group, columns, lead, hasApprovedRuns)}
                        </div>
                    </section>
                );
            })}

            {!readOnly && (
                <button type="button" className="cx-addsetup" onClick={addGroup}>
                    <i className="bi bi-plus-lg" aria-hidden="true"></i> Add another setup
                    <span className="cx-optional">for a different solution, collector or needle</span>
                </button>
            )}

            {/* Modals */}
            {
                modalState.isOpen && currentModal && (
                    <>
                        {modalState.type === 'solutionPrep' && <SolutionPreparationModal onClose={closeModal} onSave={currentModal.save} setup={currentModal.data} />}
                        {modalState.type === 'collector' && <CollectorModal isOpen={true} onClose={closeModal} onSave={currentModal.save} collector={currentModal.data} />}
                        {modalState.type === 'needle' && <NeedleModal isOpen={true} onClose={closeModal} onSave={currentModal.save} needleData={currentModal.data} />}

                        {modalState.type === 'fiberMorphology' && (
                            <FiberMorphologyModal
                                isOpen={true}
                                onClose={closeModal}
                                onSave={(d) => currentModal.save(d)}
                                initialSelection={transformSavedData(currentModal.data)}
                            />
                        )}
                        {modalState.type === 'uploadImage' && (
                            <UploadImageModal
                                isOpen={true}
                                onClose={closeModal}
                                onSave={currentModal.save}
                                initialImages={groupImagesByType(currentModal.data)}
                            />
                        )}
                        {modalState.type === 'processInstability' && (
                            <ProcessInstabilityModal
                                isOpen={true}
                                onClose={closeModal}
                                onSave={currentModal.save}
                                initialSelection={currentModal.data}
                            />
                        )}
                        {modalState.type === 'propertyAnalysis' && (
                            <PropertyAnalysisModal
                                isOpen={true}
                                onClose={closeModal}
                                onSave={currentModal.save}
                                initialMechanical={currentModal.data.mechanical}
                                initialFunctional={currentModal.data.functional}
                                initialApplication={currentModal.data.application}
                            />
                        )}
                    </>
                )
            }

            <div className="cx-actionbar" role="region" aria-label="Submit">
                {problemsOpen && problems.length > 0 && (
                    <div className="cx-actionbar__inner mb-2">
                        <ul className="small mb-0 ps-3" style={{ color: 'var(--cx-warning)' }}>
                            {problems.slice(0, 8).map((p, i) => <li key={i}>{p}</li>)}
                            {problems.length > 8 && <li>…and {problems.length - 8} more</li>}
                        </ul>
                    </div>
                )}
                <div className="cx-actionbar__inner">
                    <div className="cx-actionbar__status">
                        {onBack && (
                            <button type="button" className="cx-btn cx-btn--ghost cx-btn--sm cx-hide-narrow" onClick={onBack}>
                                <i className="bi bi-arrow-left" aria-hidden="true"></i> Publication
                            </button>
                        )}
                        <span className="cx-hide-narrow">{groups.length} setup{groups.length === 1 ? '' : 's'} · {runCount} run{runCount === 1 ? '' : 's'}</span>
                        {problems.length > 0 ? (
                            <button type="button" className="cx-btn cx-btn--ghost cx-btn--sm is-todo"
                                onClick={() => { setShowMissing(true); setProblemsOpen(o => !o); }} aria-expanded={problemsOpen}>
                                <i className="bi bi-exclamation-circle" aria-hidden="true"></i> {problems.length} to complete
                            </button>
                        ) : (
                            <span className="is-ok"><i className="bi bi-check-circle" aria-hidden="true"></i> Ready to submit</span>
                        )}
                    </div>
                    <div className="cx-actionbar__actions">
                        {requireConsent && (
                            <label className="cx-check">
                                <input type="checkbox" checked={consent} onChange={e => onConsentChange && onConsentChange(e.target.checked)} />
                                <span>I agree to the <Link to="/terms" target="_blank" rel="noreferrer">Terms of Use</Link> and <Link to="/license" target="_blank" rel="noreferrer">License Agreement</Link></span>
                            </label>
                        )}
                        <button type="button" className="cx-btn cx-btn--ghost cx-hide-narrow" onClick={handleCancel}>Cancel</button>
                        <button type="button" className="cx-btn cx-btn--primary" onClick={handleSubmit}>
                            {submitLabel} <i className="bi bi-send" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
